/**
 * 帧时间与总帧数（离线导出）。
 *
 * ## 边界策略（固定，避免各编码器各自推算）
 *
 * - **总帧数**：`totalFrames = max(1, ceil(durationSec * fps))`。
 *   在时长为正的区间内，至少输出一帧（t=0）。
 * - **第 i 帧时刻**：`t(i) = min(durationSec, i / fps)`，其中 `i ∈ [0, totalFrames - 1]`。
 *
 * 这样在均匀 fps 网格上采样，且最后一帧时间不越过 `durationSec`。
 * 当 `durationSec * fps` 非整数时，最后一帧时刻可能略小于 `durationSec`（与纯 ceil 网格一致）。
 */

/**
 * 规范化时长与帧率，避免 NaN/非有限值。
 *
 * @param durationSec - 导出时长（秒）
 * @param fps - 帧率
 * @returns 有效时长与非负有限 fps
 */
function clampDurationAndFps(durationSec: number, fps: number): { d: number; f: number } {
  const d = Number(durationSec)
  const f = Number(fps)
  const dOk = Number.isFinite(d) && d > 0 ? d : 0
  const fOk = Number.isFinite(f) && f > 0 ? f : 30
  return { d: dOk, f: fOk }
}

/**
 * 根据时长与帧率计算导出总帧数。
 *
 * @param durationSec - 导出时长（秒）
 * @param fps - 帧率（≥1 的整数期望，非整数会向下取整为至少 1）
 * @returns 总帧数 ≥ 1
 */
export function computeTotalFrames(durationSec: number, fps: number): number {
  const { d, f } = clampDurationAndFps(durationSec, fps)
  const fpsInt = Math.max(1, Math.round(f))
  if (d <= 0) return 1
  return Math.max(1, Math.ceil(d * fpsInt))
}

/**
 * 由帧索引推导该帧对应的全局时间轴时刻（秒）。
 *
 * @param frameIndex - 帧索引，0 .. totalFrames-1
 * @param durationSec - 导出时长（秒）
 * @param fps - 帧率
 * @returns  clamp 到 `[0, durationSec]` 的时刻
 */
export function timeSecondsForFrame(frameIndex: number, durationSec: number, fps: number): number {
  const { d, f } = clampDurationAndFps(durationSec, fps)
  const fpsInt = Math.max(1, Math.round(f))
  const i = Math.max(0, Math.floor(Number(frameIndex)))
  if (d <= 0) return 0
  const raw = i / fpsInt
  return Math.min(d, Math.max(0, raw))
}
